import React, { useState, useEffect, useContext } from "react";
import { api } from "../../config/api";
import AlertContext from "../../contexts/AlertContext";
import DupLoginContext from "../../contexts/DupLoginContext";
import "./userPermissions.scss";

interface Permission {
  permission_id: number;
  permission_name: string;
  permission_title: string;
  permission_subtitle: string;
  permission_description: string;
  expiry_date: string | null;
  is_expired: boolean;
  is_active: boolean;
  category: string | null;
  created_at: string;
  updated_at: string;
}

interface PaymentHistory {
  payment_id: number;
  product_name: string;
  product_price: number;
  paid_at: number;
  start_date: string | null;
  expire_date: string | null;
  payment_method: string;
}

interface UserPermissionsData {
  user_id: number;
  user_email: string;
  permissions: Permission[];
  payment_history: PaymentHistory[];
}

const UserPermissions: React.FC = () => {
  const alertContext = useContext(AlertContext);
  const dupLogin = useContext(DupLoginContext);

  const [permissionsData, setPermissionsData] = useState<UserPermissionsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<'permissions' | 'history'>('permissions');

  useEffect(() => {
    loadUserPermissions();
  }, []);

  const loadUserPermissions = async () => {
    try {
      setIsLoading(true);
      
      // Phase 6: 사용자 권한 조회 API 호출
      const response = await dupLogin.checkDupLogin(() =>
        api.getUserPermissionsAsync()
      );

      if (response?.status === 200 && response.data?.code === 1) {
        setPermissionsData(response.data.data);
      } else {
        alertContext.showAlert("권한 정보를 불러올 수 없습니다.");
      }
    } catch (error) {
      console.error("권한 정보 로드 실패:", error);
      alertContext.showAlert("권한 정보 로드 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR');
  };

  const formatDateTime = (timestamp: number) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('ko-KR');
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ko-KR').format(price);
  };

  if (isLoading) {
    return (
      <div className="user-permissions">
        <div className="permissions-header">
          <h2>내 권한 정보</h2>
        </div>
        <div className="loading">권한 정보를 불러오는 중...</div>
      </div>
    );
  }

  if (!permissionsData) {
    return (
      <div className="user-permissions">
        <div className="permissions-header">
          <h2>내 권한 정보</h2>
        </div>
        <div className="error">권한 정보를 불러올 수 없습니다.</div>
      </div>
    );
  }

  return (
    <div className="user-permissions">
      <div className="permissions-header">
        <h2>내 권한 정보</h2>
        <p>{permissionsData.user_email}님의 권한 현황</p>
      </div>

      <div className="permissions-tabs">
        <button
          className={`tab ${selectedTab === 'permissions' ? 'active' : ''}`}
          onClick={() => setSelectedTab('permissions')}
        >
          보유 권한
        </button>
        <button
          className={`tab ${selectedTab === 'history' ? 'active' : ''}`}
          onClick={() => setSelectedTab('history')}
        >
          결제 이력
        </button>
      </div>

      {selectedTab === 'permissions' && (
        <div className="permissions-content">
          {permissionsData.permissions.length === 0 ? (
            <div className="empty-state">
              <p>보유하고 있는 권한이 없습니다.</p>
            </div>
          ) : (
            <div className="permissions-list">
              {permissionsData.permissions.map((permission) => (
                <div
                  key={permission.permission_id}
                  className={`permission-item ${permission.is_active ? 'active' : 'expired'}`}
                >
                  <div className="permission-header">
                    <div className="permission-title">
                      <h3>{permission.permission_title}</h3>
                      <span className={`status-badge ${permission.is_active ? 'active' : 'expired'}`}>
                        {permission.is_active ? '이용중' : '만료됨'}
                      </span>
                    </div>
                    <div className="permission-category">
                      {permission.category && (
                        <span className="category">{permission.category}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="permission-details">
                    <p className="subtitle">{permission.permission_subtitle}</p>
                    <p className="description">{permission.permission_description}</p>
                  </div>

                  <div className="permission-dates">
                    <div className="date-item">
                      <span className="label">만료일:</span>
                      <span className="value">
                        {formatDate(permission.expiry_date)}
                        {permission.is_expired && <span className="expired-text"> (만료됨)</span>}
                      </span>
                    </div>
                    <div className="date-item">
                      <span className="label">등록일:</span>
                      <span className="value">{formatDate(permission.created_at)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedTab === 'history' && (
        <div className="history-content">
          {permissionsData.payment_history.length === 0 ? (
            <div className="empty-state">
              <p>결제 이력이 없습니다.</p>
            </div>
          ) : (
            <div className="history-list">
              {permissionsData.payment_history.map((history) => (
                <div key={history.payment_id} className="history-item">
                  <div className="history-header">
                    <h3>{history.product_name}</h3>
                    <span className="price">{formatPrice(history.product_price)}원</span>
                  </div>
                  
                  <div className="history-details">
                    <div className="detail-item">
                      <span className="label">결제일:</span>
                      <span className="value">{formatDateTime(history.paid_at)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">결제 방법:</span>
                      <span className="value">{history.payment_method}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">이용 기간:</span>
                      <span className="value">
                        {formatDate(history.start_date)} ~ {formatDate(history.expire_date)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserPermissions;